/**
 * Registry de tururi ghidate. Fiecare tour are ID, pași și (opțional) configurare
 * pentru welcome modal. TourTriggerMount și useTour se bazează pe acest registry.
 */
import type { Permission } from '@softfactura/shared';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { DREPT_IMPORT_CATEGORII, DREPT_IMPORT_PLATFORMA } from '@/features/migration/import-permissions';

export type TourStep = {
  id: string;
  selector: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'over';
  /** Override explicit pentru align — util pentru elemente mici la marginea viewport-ului
   *  (ex: buton top-right cu placement='bottom' + align='end' = tooltip dedesubt aliniat dreapta). */
  align?: 'start' | 'center' | 'end';
  /** Numele grupului din sidebar care trebuie deschis înainte ca elementul țintă să fie disponibil. */
  sidebarGroup?: string;
  /** Opțional — URL-ul pe care trebuie să fie utilizatorul pentru ca acest pas să fie valid (folosit pt navigare). */
  requiresPath?: string;
  /**
   * Dacă true, pasul e EXCLUS din tur când ținta nu e vizibilă la momentul rulării
   * (vezi getEffectiveTourSteps). Pentru ținte care există doar condiționat — ex.
   * butonul Setup checklist se auto-ascunde la 100% configurare; fără skip, pasul
   * ar arăta spre un punct gol pe conturile complet configurate.
   */
  skipIfHidden?: boolean;
  /**
   * Pasul apare doar cu acest drept (la o listă, cu oricare dintre ele): ținta lui se randează doar cu el. Spre
   * deosebire de `skipIfHidden`, nu depinde de DOM la pornirea turului (navigarea spre pagină poate fi încă în curs).
   */
  requiresPermission?: Permission | readonly Permission[];
};

export type TourDefinition = {
  id: string;
  /** Pașii ordonați ai turului. */
  steps: TourStep[];
  /** Pe ce ruta utilizatorul ar trebui să fie când pornește turul (folosit pt auto-trigger și redirect). */
  startPath?: string;
  /**
   * Dacă true, restart()/openWelcome() afișează WelcomeModal înainte de spotlight.
   * Dacă false/undefined, sare direct la phase='running'. Folosit pentru mini-tururi
   * declanșate de utilizator dintr-un context clar (banner/buton dedicat).
   */
  hasWelcomeModal?: boolean;
  /** Toast de celebrare + CTA next-step afișat după ce user-ul finalizează turul. */
  completion?: {
    message: string;
    cta?: { label: string; href?: string };
  };
};

// ─── OVERVIEW (welcome tour, 4 pași — sidebar) ──────────────
const OVERVIEW: TourDefinition = {
  id: 'overview',
  startPath: '/',
  hasWelcomeModal: true,
  completion: {
    message: 'Felicitări! Ai văzut meniurile principale.',
    cta: { label: 'Configurează contul →', href: '/setari' },
  },
  steps: [
    {
      id: 'invoices',
      selector: '[data-tour-scope="desktop"] [data-tour="sidebar-invoices"]',
      title: 'Facturi',
      body: 'Emiti și gestionezi facturile fiscale. Acces rapid la storno, e-Factura ANAF și PDF.',
      placement: 'right',
      sidebarGroup: 'Vânzări',
    },
    {
      id: 'clients',
      selector: '[data-tour-scope="desktop"] [data-tour="sidebar-clients"]',
      title: 'Clienți',
      body: 'Salvați automat la prima factură. Datele fiscale se completează din ANAF după CUI.',
      placement: 'right',
      sidebarGroup: 'Vânzări',
    },
    {
      id: 'products',
      selector: '[data-tour-scope="desktop"] [data-tour="sidebar-products"]',
      title: 'Produse',
      body: 'Catalog cu prețuri, TVA și unități. Selectezi rapid când creezi factura.',
      placement: 'right',
      // 'Vânzări' = numele REAL al grupului (ca la invoices/clients). Istoric aici era
      // 'Nomenclatoare' (grup fantomă, nomenclatoarele mutate în /setari) → pin pe grup
      // inexistent → Vânzări închis → spotlight pica pe alt item. NU pune aici un grup
      // care nu există în navigation-ul din sidebar.tsx.
      sidebarGroup: 'Vânzări',
    },
    {
      id: 'setup-checklist',
      selector: '[data-tour="topbar-setup-checklist"]',
      title: 'Configurare cont',
      body: 'Pașii rămași pentru setup complet. Click oricând să vezi progresul.',
      placement: 'left',
      // Butonul se auto-ascunde la 100% configurare → pe conturi complet configurate
      // pasul dispare din tur (3 pași în loc de 4); userii noi îl văd normal.
      skipIfHidden: true,
    },
  ],
};

// ─── FIRST INVOICE (mini-tur, 4 pași — pe /facturi/nou) ──────
const FIRST_INVOICE: TourDefinition = {
  id: 'first-invoice',
  startPath: '/facturi/nou',
  completion: {
    message: 'Acum știi cum se emite o factură. Hai să încercăm cu una reală!',
    cta: { label: 'Emite prima factură →', href: '/facturi/nou' },
  },
  steps: [
    {
      id: 'header',
      selector: '[data-tour="invoice-header"]',
      title: 'Datele documentului',
      body: 'Serie + număr (auto), client (cu auto-completare ANAF după CUI), date emitere și scadență.',
      placement: 'bottom',
    },
    {
      id: 'lines',
      selector: '[data-tour="invoice-lines"]',
      title: 'Produse / servicii',
      body: 'Selectezi din catalog sau adaugi ad-hoc. TVA calculat automat (21% / 11% / 5% / 0% scutit).',
      placement: 'top',
    },
    {
      id: 'totals',
      selector: '[data-tour="invoice-totals"]',
      title: 'Totaluri & opțiuni',
      body: 'Subtotal, TVA defalcat pe cote, total. Aici reduceri globale + opțiuni emitere (chitanță, e-Factura, stoc).',
      placement: 'top',
    },
    {
      id: 'save',
      selector: '[data-tour="invoice-save"]',
      title: 'Salvează & emite',
      body: 'Ciornă (modificabilă) sau Emite (definitiv). La emitere → ANAF în max 5 zile lucrătoare (Codul Fiscal art. 319).',
      placement: 'top',
    },
  ],
};

// ─── NIR RECEPTION (mini-tur, 4 pași — pe /receptie/nou) ──────
const NIR_RECEPTION: TourDefinition = {
  id: 'nir-reception',
  startPath: '/receptie/nou',
  completion: {
    message: 'Acum știi cum se face o recepție NIR.',
    cta: { label: 'Înregistrează prima recepție →', href: '/receptie/nou' },
  },
  steps: [
    {
      id: 'supplier-doc',
      selector: '[data-tour="nir-supplier-doc"]',
      title: 'Furnizor & document',
      body: 'Alegi furnizorul, gestiunea (depozitul), introduci factură furnizor + dată.',
      placement: 'bottom',
    },
    {
      id: 'lines',
      selector: '[data-tour="nir-lines"]',
      title: 'Produse primite',
      body: 'Adaugi cantități + prețuri achiziție. Cod furnizor + ultimele cumpărate apar ca shortcut-uri.',
      placement: 'top',
    },
    {
      id: 'totals',
      selector: '[data-tour="nir-totals"]',
      title: 'Totaluri & plată',
      body: 'Subtotal, TVA, total. Aici plata către furnizor + comparație cantitate facturată vs primită.',
      placement: 'top',
    },
    {
      id: 'save',
      selector: '[data-tour="nir-save"]',
      title: 'Salvează & confirmă',
      body: 'Ciornă (auto-save activ) sau Confirmă (intră în stoc + generează NIR formular 14-3-1A).',
      placement: 'bottom',
    },
  ],
};

// ─── e-Factura ANAF SETUP (mini-tur, 4 pași — pe /setari?tab=efactura) ──────
const EFACTURA_SETUP: TourDefinition = {
  id: 'efactura-setup',
  startPath: '/setari?tab=efactura',
  completion: {
    message: 'Conectează-te când ești pregătit.',
    cta: { label: 'Mergi la conexiune ANAF', href: '/setari?tab=efactura' },
  },
  steps: [
    {
      id: 'config',
      selector: '[data-tour="efactura-config"]',
      title: 'Conexiune ANAF',
      body: 'Aici alegi metoda: OAuth (recomandat — 1 click cu certificat digital), prin contabil, sau upload manual .p12/.pfx.',
      placement: 'top',
    },
    {
      id: 'autosubmit',
      selector: '[data-tour="efactura-autosubmit"]',
      title: 'Trimitere automată',
      body: 'Activezi auto-submit → factura ajunge la ANAF imediat după emitere. Termen legal: 5 zile lucrătoare.',
      placement: 'top',
    },
    {
      id: 'stats',
      selector: '[data-tour="efactura-stats"]',
      title: 'Statistici',
      body: 'Trimise · Validate · Respinse · Primite. Click pe oricare pentru filtrare.',
      placement: 'bottom',
    },
    {
      id: 'tabs',
      selector: '[data-tour="efactura-tabs"]',
      title: 'Istoric',
      body: 'Comuți între facturile trimise (de tine la ANAF) și cele primite (de la furnizori). Click pe rând → detalii + erori.',
      placement: 'bottom',
    },
  ],
};

// ─── EXPENSE NEW (mini-tur, 3 pași — în modalul rapid din /cheltuieli) ──────
// Etapa 2 unificare: /cheltuieli/nou e redirect; ancorele trăiesc în
// ExpenseQuickModal/ExpenseQuickForm, iar pornirea (banner + top-bar) deschide
// întâi modalul (?nou=1 sau onStart).
const EXPENSE_NEW: TourDefinition = {
  id: 'expense-new',
  startPath: '/cheltuieli?nou=1',
  completion: {
    message: 'Acum știi cele 5 tipuri de cheltuieli.',
    cta: { label: 'Adaugă prima cheltuială →', href: '/cheltuieli?nou=1' },
  },
  steps: [
    {
      id: 'header',
      selector: '[data-tour="expense-header"]',
      title: 'Cheltuială nouă',
      body: 'Orice document de la furnizor: factură, bon fiscal, ordin de plată, chitanță.',
      placement: 'bottom',
    },
    {
      id: 'types',
      selector: '[data-tour="expense-types"]',
      title: 'Alege tipul',
      body: '🧾 Factură (serie/număr + scadență; „Detalii complete" pentru linii) · ⛽ Bon fiscal (combustibil/consumabile) · 🏛️ Ordin de plată (taxe/bancă) · 💵 Chitanță (cash) · 📋 Altă cheltuială.',
      placement: 'bottom',
    },
    {
      id: 'save',
      selector: '[data-tour="expense-quick-save"]',
      title: 'Sumă, furnizor, categorie → Salvează',
      body: 'Completezi totalul (TVA se calculează invers), furnizorul (text liber sau din listă) și categoria contabilă. Poți înregistra și plata pe loc.',
      placement: 'top',
    },
  ],
};

// ─── DATA IMPORT (mini-tur, 3 pași — pe /migrare) ──────
const DATA_IMPORT: TourDefinition = {
  id: 'data-import',
  startPath: '/migrare',
  completion: {
    message: 'Migrarea e simplă. Alegi platforma sau categoria și pornești.',
    cta: { label: 'Începe primul import →', href: '/migrare' },
  },
  steps: [
    {
      id: 'header',
      selector: '[data-tour="migration-header"]',
      title: 'Migrare date',
      body: 'Importă din alt program de facturare sau direct din CSV/XLSX. Tipic sub 5 minute.',
      placement: 'bottom',
    },
    {
      id: 'platforms',
      selector: '[data-tour="migration-platforms"]',
      title: 'De unde migrezi?',
      body: 'API direct: Smartbill · Oblio · FGO · FacturisOnline. Pentru altele: cere adăugare + atașează fișier sample.',
      placement: 'top',
      // grila apare doar cu dreptul migrării de pe alt program
      requiresPermission: DREPT_IMPORT_PLATFORMA,
    },
    {
      id: 'entities',
      selector: '[data-tour="migration-entities"]',
      title: 'Sau pe categorie',
      body: 'Stocuri · Clienți · Produse · Furnizori. Wizard separat per tip cu mapping coloane + validare înainte de import.',
      placement: 'top',
      // secțiunea apare doar cu dreptul unuia dintre importurile pe categorii
      requiresPermission: DREPT_IMPORT_CATEGORII,
    },
  ],
};

// ─── PAYMENT RECEIVE (mini-tur, 3 pași — pe /incasari) ──────
const PAYMENT_RECEIVE: TourDefinition = {
  id: 'payment-receive',
  startPath: '/incasari',
  completion: {
    message: 'Acum știi fluxul încasări.',
    cta: { label: 'Înregistrează prima încasare →', href: '/incasari' },
  },
  steps: [
    {
      id: 'create',
      selector: '[data-tour="payments-create-btn"]',
      title: 'Înregistrare încasare',
      body: 'Numerar · Transfer bancar · Card · Ramburs curier · CEC. Legat de o factură sau chitanță separată.',
      placement: 'bottom',
      align: 'end',
    },
    {
      id: 'filters',
      selector: '[data-tour="payments-filters"]',
      title: 'Filtre rapide',
      body: 'Caută client/factură/observații. Filtrezi după status (complet/parțial) și tip încasare.',
      placement: 'bottom',
    },
    {
      id: 'table',
      selector: '[data-tour="payments-table"]',
      title: 'Istoric încasări',
      body: 'Click pe rând → detalii sau emitere chitanță. Export Excel pentru contabilitate.',
      placement: 'top',
    },
  ],
};

export const TOURS: Record<string, TourDefinition> = {
  overview: OVERVIEW,
  'first-invoice': FIRST_INVOICE,
  'nir-reception': NIR_RECEPTION,
  'efactura-setup': EFACTURA_SETUP,
  'expense-new': EXPENSE_NEW,
  'data-import': DATA_IMPORT,
  'payment-receive': PAYMENT_RECEIVE,
};

export function getTour(tourId: string): TourDefinition | null {
  return TOURS[tourId] ?? null;
}

/**
 * Pașii EFECTIVI ai unui tur pentru sesiunea curentă: exclude pașii `requiresPermission`
 * fără dreptul cerut și pașii `skipIfHidden` a căror țintă nu e vizibilă în DOM acum (ex.
 * Setup checklist ascuns la 100% configurare). Pentru useri noi lista e identică cu registry-ul.
 * ⚠ TOȚI consumatorii (TourTriggerMount, store.next/goTo, useTour) trebuie să
 * folosească această funcție, NU tour.steps direct — altfel indexurile diverg.
 */
export function getEffectiveTourSteps(tourId: string): TourStep[] {
  const tour = getTour(tourId);
  if (!tour) return [];
  const granted = new Set<Permission>(useAuthStore.getState().user?.permissions ?? []);
  const steps = tour.steps.filter((s) => {
    const cerut = s.requiresPermission;
    if (!cerut) return true;
    return typeof cerut === 'string' ? granted.has(cerut) : cerut.some((p) => granted.has(p));
  });
  if (typeof document === 'undefined') return steps;
  return steps.filter((s) => {
    if (!s.skipIfHidden) return true;
    const el = document.querySelector<HTMLElement>(s.selector);
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.width >= 5 && r.height >= 5;
  });
}
